import React, { useCallback, useEffect, useState } from "react"
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    CircularProgress,
    Box,
    Typography,
    IconButton,
} from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import { useTranslation } from "react-i18next"

interface DocumentPreviewDialogProps {
    open: boolean
    onClose: () => void
    html: string | null
    loading: boolean
    documentNumber?: string
    documentType?: string
}

const DocumentPreviewDialog: React.FC<DocumentPreviewDialogProps> = ({
    open,
    onClose,
    html,
    loading,
    documentNumber,
    documentType,
}) => {
    const { t } = useTranslation()
    const isReceipt = documentType === "receipt"
    const [iframeHeight, setIframeHeight] = useState<number>(0)

    useEffect(() => {
        setIframeHeight(0)
    }, [html])

    const handleIframeLoad = useCallback(
        (e: React.SyntheticEvent<HTMLIFrameElement>) => {
            const iframeDoc = e.currentTarget.contentDocument
            if (iframeDoc?.body) {
                const bodyHeight = iframeDoc.body.scrollHeight || 0
                const htmlHeight = iframeDoc.documentElement?.scrollHeight || 0
                setIframeHeight(Math.max(bodyHeight, htmlHeight))
            }
        },
        []
    )

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth={!isReceipt}
            maxWidth={isReceipt ? false : "md"}
            PaperProps={{
                sx: isReceipt
                    ? {
                          width: "340px",
                          maxWidth: "340px",
                          m: 2,
                          maxHeight: "90vh",
                      }
                    : {},
            }}
        >
            <DialogTitle
                sx={{
                    py: 1.5,
                    px: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    ...(isReceipt ? { fontSize: "0.85rem" } : {}),
                }}
            >
                <span>
                    {t("exportsComponents.documentPreview.title")}
                    {documentNumber ? ` — ${documentNumber}` : ""}
                </span>
                {isReceipt && (
                    <IconButton onClick={onClose} size="small" sx={{ ml: 1 }}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                )}
            </DialogTitle>
            <DialogContent
                dividers
                sx={{
                    p: 0,
                    ...(isReceipt
                        ? {
                              overflowY: "auto",
                              overflowX: "hidden",
                              maxHeight: "80vh",
                              flex: "none",
                          }
                        : { overflow: "hidden", height: "75vh" }),
                }}
            >
                {loading ? (
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            py: 6,
                        }}
                    >
                        <CircularProgress />
                    </Box>
                ) : html ? (
                    <iframe
                        srcDoc={html}
                        onLoad={handleIframeLoad}
                        scrolling={isReceipt ? "no" : undefined}
                        style={{
                            width: "100%",
                            height: isReceipt
                                ? `${Math.max(iframeHeight, 1)}px`
                                : "100%",
                            border: "none",
                            display: "block",
                            overflow: isReceipt ? "hidden" : "auto",
                        }}
                        title={String(
                            t("exportsComponents.documentPreview.title")
                        )}
                    />
                ) : (
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ py: 4, textAlign: "center" }}
                    >
                        {t("exportsComponents.documentPreview.noPreview")}
                    </Typography>
                )}
            </DialogContent>
            {!isReceipt && (
                <DialogActions>
                    <Button onClick={onClose}>
                        {t("exportsComponents.documentPreview.close")}
                    </Button>
                </DialogActions>
            )}
        </Dialog>
    )
}

export default DocumentPreviewDialog
